import React, { useContext, useEffect, useState } from 'react'
import { Box } from '../../../../../components/Box/Box'
import { H3 } from '../../../../../_components/Typography/Components/Headings/H3'
import { Grid, GridCol } from '../../../../../components/Grid'

import { DebtorsContacts } from './DebtorsContacts/DebtorsContacts'
import { ClientContacts } from './ClientContacts'
import { RequestDocuments } from './RequestDocuments'

import ScheduleESigningContext, { ScheduleESigningProvider } from './context'
import {
  createSignatureRequest,
  getHelloSignTemplates,
  previewSignatureRequest,
} from '../../../../../_services/v2/documents.service'
import { downloadPdfFile } from '../../../../../_helpers/download'
import { useNotifications } from '../../../../../_reduxHooks/notifications.hook'
import { LoaderButton } from '../../../../../_components/LoaderButton'
import { RenderIf } from '../../../../../components/helpers/RenderIf/RenderIf'
import { getScheduleNotifiableDebtors } from '../../../../../_services/lender.service'
import { getContact } from '../../../../../_services/debtor.service'

const RequestSignatures = ({ schedule, createdDocs, setCreatedDocs }) => {
  const { addEntity, entity, template_id, signers } = useContext(ScheduleESigningContext)
  const [isLoading, setIsLoading] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [documents, setDocuments] = useState([])
  const [debtors, setDebtors] = useState([])
  const { showNotification } = useNotifications()

  const businessId = schedule.business_id
  const businessName = schedule.business_name
  const scheduleId = schedule.id

  useEffect(() => {
    addEntity({
      id: schedule.id,
      type: 'InvoiceSchedule',
    })
    getScheduleNotifiableDebtors(scheduleId).then(async (debtorsWithContacts) => {
      const contacts = await Promise.all(debtorsWithContacts.map((debtor) => getContact(debtor.id, null, null)))

      if (contacts.length > 0) {
        const debtors = debtorsWithContacts.map((debtor, index) => {
          return {
            ...debtor,
            contacts: contacts[index],
          }
        })
        setDebtors(debtors)
      }
    })
  }, [schedule.id, templateName])

  useEffect(() => {
    const showDocument = (document) => {
      if (document.name === 'capital_plus_purchased_doc') {
        return schedule.state === 'purchased'
      }
      return true
    }
    getHelloSignTemplates().then((resp) => setDocuments(resp.filter((doc) => showDocument(doc))))
  }, [schedule.state])

  const handleCreateRequest = async () => {
    const data = {
      entity: entity,
      business_id: businessId,
      documents: [
        {
          template_id: template_id,
          signers: Object.keys(signers).map((key) => {
            const signer = signers[key]
            return {
              id: signer.id,
              role: signer.role,
              email: signer.email || signer.email_address,
              title: signer.title,
              phone_number: signer.phone_number,
              name: [signer.first_name, signer.last_name].join(' '),
            }
          }),
        },
      ],
    }
    setIsLoading(true)
    await createSignatureRequest(data)
      .then(() => {
        setIsLoading(false)
        showNotification({
          type: `success`,
          message: 'Signature request successfully created',
        })
        setCreatedDocs([...createdDocs, data])
      })
      .catch((error) => {
        setIsLoading(false)
        showNotification({
          type: `error`,
          message: error,
          closeOnClick: true,
        })
      })
  }

  const handlePreviewRequest = async () => {
    const data = {
      entity: entity,
      business_id: businessId,
      documents: [
        {
          template_id: template_id,
          signers: Object.keys(signers).map((key) => {
            const signer = signers[key]
            return {
              id: signer.id,
              role: signer.role,
              email: signer.email || signer.email_address,
              title: signer.title,
              phone_number: signer.phone_number,
              name: [signer.first_name, signer.last_name].join(' '),
            }
          }),
        },
      ],
    }
    setIsLoading(true)
    await previewSignatureRequest(data)
      .then((response) => {
        setIsLoading(false)
        downloadPdfFile(response, entity.id)
      })
      .catch((error) => {
        setIsLoading(false)
        showNotification({
          type: `error`,
          message: error,
          closeOnClick: true,
        })
      })
  }

  const isDisabled = () => {
    const selected_contact_types = []

    Object.values(signers).forEach((value) => {
      selected_contact_types.push(value.role)
    })

    const document = documents.find((document) => document.name === templateName)
    let allSignersSelected = null

    if (document) {
      allSignersSelected = document.signers
        .filter((s) => s.signer !== 'Tenant')
        .every((prop) => selected_contact_types.includes(prop.signer))
    }

    return !template_id || !allSignersSelected
  }

  const isFieldEnabled = (signerType) => {
    const document = documents.find((document) => document.name === templateName)
    if (document) {
      return document.signers.some((prop) => prop.signer === signerType)
    }
    return false
  }

  return (
    <Box mt={'40px'}>
      <H3 fw={400} mb={'15px'}>
        Requesting Debtors and Clients Signatures
      </H3>
      <Box borderRadius={'3px'} borderWidth={'1px'} p={'20px'}>
        <Grid columns={10}>
          <GridCol xs={3}>
            <Box>
              <H3 mb={'10px'}>Signing Documents</H3>

              <Box pb={'5px'} pt={'5px'}>
                <Grid columns={2}>
                  <GridCol xs={1.8}>
                    <RequestDocuments documents={documents} setTemplateName={setTemplateName} />
                  </GridCol>
                </Grid>
              </Box>
            </Box>
          </GridCol>
          <GridCol xs={5}>
            <Box bwl={'1px'} bwr={'1px'}>
              <Box pl={'30px'}>
                <RenderIf condition={isFieldEnabled('Debtor')}>
                  <H3 mb={'10px'}>Debtors</H3>
                  <DebtorsContacts businessId={businessId} debtors={debtors} scheduleId={scheduleId} />
                </RenderIf>

                <RenderIf condition={isFieldEnabled('Business')}>
                  <H3 mb={'10px'} mt={'10px'}>
                    Client
                  </H3>
                  <ClientContacts businessId={businessId} businessName={businessName} templateName={templateName} />
                </RenderIf>
              </Box>
            </Box>
          </GridCol>
          <GridCol xs={2}>
            <Box mr={'10px'} mt={'36px'}>
              <LoaderButton
                className={'full-width'}
                disabled={isDisabled()}
                isLoading={isLoading}
                onClick={handlePreviewRequest}
                text={'Preview Document'}
              />
            </Box>
            <Box mr={'10px'} mt={'20px'}>
              <LoaderButton
                className={'full-width'}
                disabled={isDisabled()}
                isLoading={isLoading}
                onClick={handleCreateRequest}
                text={'Request Signature(s)'}
              />
            </Box>
          </GridCol>
        </Grid>
      </Box>
    </Box>
  )
}

export const InvoiceScheduleESigning = ({ schedule, createdDocs, setCreatedDocs }) => {
  return (
    <ScheduleESigningProvider>
      <RequestSignatures createdDocs={createdDocs} schedule={schedule} setCreatedDocs={setCreatedDocs} />
    </ScheduleESigningProvider>
  )
}
